import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { isNullish } from 'remeda';
import type { AddressSummary, GenerateAddressSummaryResponse } from 'src/gql/graphql';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import type { Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import {
  GENERATE_ADDRESS_SUMMARY_MUTATION,
  GET_ADDRESS_SUMMARY_QUERY,
} from './address-summary.operations';
import { parseSiteOverview, type SiteOverviewSummary } from './parse-site-overview';

interface GetAddressSummaryData {
  getAddressSummary?: Records<AddressSummary>;
}

interface GenerateAddressSummaryData {
  generateAddressSummary?: GenerateAddressSummaryResponse;
}

export type SiteOverviewProblem = 'missing' | 'unreadable';

export interface SiteOverviewResult {
  overview: SiteOverviewSummary | null;
  shortSummary: string | null;
  updatedAt: string | null;
  problem: SiteOverviewProblem | null;
  loading: boolean;
  allowed: boolean;
}

const EMPTY_RESULT: SiteOverviewResult = {
  overview: null,
  shortSummary: null,
  updatedAt: null,
  problem: null,
  loading: false,
  allowed: false,
};

export function useSiteOverview(addressId: string | undefined): SiteOverviewResult {
  const { checkValidity, loading: rulesLoading } = useGroupRules();
  const allowed = checkValidity([UserGroupRules.AccessToAiSiteSummary]);
  const partnerId = useGlobalStore.use.selectedPartnerId();
  const skip = !addressId || rulesLoading || !allowed;

  const { data, loading } = useQuery<GetAddressSummaryData>(GET_ADDRESS_SUMMARY_QUERY, {
    skip,
    // Skip the cache. The summary can get rewritten while you're still on the site
    // and we'd keep showing the old one.
    fetchPolicy: 'no-cache',
    onError: (queryError) => {
      console.error('[siteOverview] getAddressSummary failed', queryError.message, queryError);
    },
    variables: {
      params: {
        filters: [{ attribute: 'addressId', operator: '=', value: addressId }],
        sorting: [{ attribute: 'updatedAt', desc: true }],
        limit: 1,
        offset: 0,
      },
    },
  });

  const [generateAddressSummary, generateState] = useMutation<GenerateAddressSummaryData>(
    GENERATE_ADDRESS_SUMMARY_MUTATION,
    {
      onError: (mutationError) => {
        console.error(
          '[siteOverview] generateAddressSummary failed',
          mutationError.message,
          mutationError,
        );
      },
    },
  );

  useEffect(() => {
    if (skip || !addressId || isNullish(partnerId)) {
      return;
    }
    void generateAddressSummary({
      variables: { input: { addressId, partnerId } },
    });
  }, [addressId, generateAddressSummary, partnerId, skip]);

  return useMemo(() => {
    if (rulesLoading) {
      return { ...EMPTY_RESULT, loading: true };
    }
    if (!allowed) {
      return EMPTY_RESULT;
    }
    if (loading) {
      return { ...EMPTY_RESULT, allowed: true, loading: true };
    }

    const generated = generateState.data?.generateAddressSummary;
    const fetched = data?.getAddressSummary?.records?.[0];
    const generatedForSite = generated?.addressId === addressId ? generated : undefined;
    const fetchedForSite = fetched?.addressId === addressId ? fetched : undefined;
    const record = generatedForSite ?? fetchedForSite;
    const waitingOnGenerate =
      !record &&
      !skip &&
      !isNullish(partnerId) &&
      (generateState.loading || !generateState.called);
    if (waitingOnGenerate) {
      return { ...EMPTY_RESULT, allowed: true, loading: true };
    }
    if (!record) {
      return { ...EMPTY_RESULT, allowed: true, problem: 'missing' };
    }

    const summary = parseSiteOverview(record.intermediateSummary);
    if (!summary) {
      return {
        ...EMPTY_RESULT,
        allowed: true,
        shortSummary: record.shortSummary ?? null,
        updatedAt: record.updatedAt ?? null,
        problem: 'unreadable',
      };
    }

    return {
      overview: summary,
      shortSummary: record.shortSummary ?? null,
      updatedAt: record.updatedAt ?? null,
      problem: null,
      loading: false,
      allowed: true,
    };
  }, [
    allowed,
    data,
    generateState.called,
    generateState.data,
    generateState.loading,
    loading,
    addressId,
    partnerId,
    rulesLoading,
    skip,
  ]);
}
